import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { TextArea } from '../ui/TextArea';
import { Table } from '../ui/Table';
import { useData } from '../../context/DataContext';
import { Invoice, Payment, Refund } from '../../types';
import { formatCurrency, formatDate } from '../../utils/calculations';
import { PaymentStatusBadge } from './StatusBadge';
import { CreditCardIcon, BanknoteIcon, AlertCircleIcon } from 'lucide-react';
interface PaymentModalProps {
  invoice: Invoice;
  isOpen: boolean;
  onClose: () => void;
}
export function PaymentModal({
  invoice,
  isOpen,
  onClose
}: PaymentModalProps) {
  const {
    createPayment,
    createRefund,
    getPaymentsByInvoiceId,
    getRefundsByInvoiceId
  } = useData();
  const [activeTab, setActiveTab] = useState<'payments' | 'refunds'>('payments');
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [isAddingRefund, setIsAddingRefund] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Payment form state
  const [paymentAmount, setPaymentAmount] = useState(invoice.total_amount.toString());
  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'cash'>('stripe');
  // Refund form state
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const payments = getPaymentsByInvoiceId(invoice.id);
  const refunds = getRefundsByInvoiceId(invoice.id);
  const totalPaid = payments.filter(p => p.status === 'succeeded').reduce((sum, p) => sum + p.amount, 0);
  const totalRefunded = refunds.reduce((sum, r) => sum + r.amount, 0);
  const balance = invoice.total_amount - totalPaid + totalRefunded;
  const handleAddPayment = async () => {
    setIsLoading(true);
    try {
      await createPayment({
        invoice_id: invoice.id,
        amount: parseFloat(paymentAmount),
        method: paymentMethod,
        status: 'succeeded',
        date: new Date()
      });
      setIsAddingPayment(false);
      setPaymentAmount(balance.toString());
    } catch (error) {
      console.error('Failed to create payment:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleAddRefund = async () => {
    setIsLoading(true);
    try {
      await createRefund({
        invoice_id: invoice.id,
        amount: parseFloat(refundAmount),
        reason: refundReason,
        date: new Date()
      });
      setIsAddingRefund(false);
      setRefundAmount('');
      setRefundReason('');
    } catch (error) {
      console.error('Failed to create refund:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const paymentColumns = [{
    key: 'date',
    header: 'Date',
    render: (payment: Payment) => formatDate(payment.date)
  }, {
    key: 'amount',
    header: 'Amount',
    render: (payment: Payment) => formatCurrency(payment.amount)
  }, {
    key: 'method',
    header: 'Method',
    render: (payment: Payment) => <span className="flex items-center gap-1.5" data-id="element-50">
          {payment.method === 'stripe' ? <CreditCardIcon className="w-4 h-4 text-blue-600" data-id="element-51" /> : <BanknoteIcon className="w-4 h-4 text-green-600" data-id="element-52" />}
          {payment.method.charAt(0).toUpperCase() + payment.method.slice(1)}
        </span>
  }, {
    key: 'status',
    header: 'Status',
    render: (payment: Payment) => <PaymentStatusBadge status={payment.status} data-id="element-53" />
  }, {
    key: 'fee',
    header: 'Fee',
    render: (payment: Payment) => payment.stripe_fee ? formatCurrency(payment.stripe_fee) : '-'
  }, {
    key: 'net',
    header: 'Net',
    render: (payment: Payment) => payment.net_amount ? formatCurrency(payment.net_amount) : formatCurrency(payment.amount)
  }];
  const refundColumns = [{
    key: 'date',
    header: 'Date',
    render: (refund: Refund) => formatDate(refund.date)
  }, {
    key: 'amount',
    header: 'Amount',
    render: (refund: Refund) => formatCurrency(refund.amount)
  }, {
    key: 'reason',
    header: 'Reason',
    render: (refund: Refund) => refund.reason
  }];
  return <Modal isOpen={isOpen} onClose={onClose} title="Payments & Refunds" size="lg" data-id="element-54">
      {/* Test Mode Banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 flex items-center gap-2" data-id="element-55">
        <AlertCircleIcon className="w-5 h-5 text-yellow-600" data-id="element-56" />
        <span className="text-sm text-yellow-800" data-id="element-57">
          Test Mode - No real charges will be made
        </span>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-58">
        <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-59">
          <p className="text-sm text-gray-500" data-id="element-60">Invoice Total</p>
          <p className="text-lg font-semibold text-gray-900" data-id="element-61">
            {formatCurrency(invoice.total_amount)}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center" data-id="element-62">
          <p className="text-sm text-green-600" data-id="element-63">Total Paid</p>
          <p className="text-lg font-semibold text-green-700" data-id="element-64">
            {formatCurrency(totalPaid)}
          </p>
        </div>
        <div className={`rounded-lg p-3 text-center ${balance > 0 ? 'bg-red-50' : 'bg-gray-50'}`} data-id="element-65">
          <p className={`text-sm ${balance > 0 ? 'text-red-600' : 'text-gray-500'}`} data-id="element-66">
            Balance Due
          </p>
          <p className={`text-lg font-semibold ${balance > 0 ? 'text-red-700' : 'text-gray-900'}`} data-id="element-67">
            {formatCurrency(balance)}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4" data-id="element-68">
        <button className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'payments' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('payments')} data-id="element-69">
          Payments ({payments.length})
        </button>
        <button className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'refunds' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('refunds')} data-id="element-70">
          Refunds ({refunds.length})
        </button>
      </div>

      {/* Content */}
      {activeTab === 'payments' && <div className="space-y-4" data-id="element-71">
          {isAddingPayment ? <div className="bg-gray-50 rounded-lg p-4 space-y-4" data-id="element-72">
              <h4 className="font-medium text-gray-900" data-id="element-73">Add Payment</h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-74">
                <Input label="Amount" type="number" min="0.01" step="0.01" max={balance} value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} data-id="element-75" />
                <Select label="Method" options={[{
            value: 'stripe',
            label: 'Credit Card (Stripe)'
          }, {
            value: 'cash',
            label: 'Cash'
          }]} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value as 'stripe' | 'cash')} data-id="element-76" />
              </div>
              {paymentMethod === 'stripe' && <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-77">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-78">
                    Card Details (Test Mode)
                  </p>
                  <div className="h-10 bg-gray-100 rounded flex items-center px-3 text-sm text-gray-400" data-id="element-79">
                    •••• •••• •••• 4242 (Test Card)
                  </div>
                  <p className="text-xs text-gray-400 mt-2" data-id="element-80">
                    Fee: 2.9% + $0.30 ={' '}
                    {formatCurrency(parseFloat(paymentAmount || '0') * 0.029 + 0.3)}
                  </p>
                </div>}
              <div className="flex justify-end gap-2" data-id="element-81">
                <Button variant="secondary" onClick={() => setIsAddingPayment(false)} data-id="element-82">
                  Cancel
                </Button>
                <Button onClick={handleAddPayment} isLoading={isLoading} disabled={!paymentAmount || parseFloat(paymentAmount) <= 0} data-id="element-83">
                  Record Payment
                </Button>
              </div>
            </div> : <Button onClick={() => {
        setPaymentAmount(balance.toString());
        setIsAddingPayment(true);
      }} disabled={balance <= 0} data-id="element-84">
              Add Payment
            </Button>}

          <Table columns={paymentColumns} data={payments} keyExtractor={p => p.id} emptyMessage="No payments recorded" data-id="element-85" />
        </div>}

      {activeTab === 'refunds' && <div className="space-y-4" data-id="element-86">
          {isAddingRefund ? <div className="bg-gray-50 rounded-lg p-4 space-y-4" data-id="element-87">
              <h4 className="font-medium text-gray-900" data-id="element-88">Issue Refund</h4>
              <Input label="Amount" type="number" min="0.01" step="0.01" max={totalPaid - totalRefunded} value={refundAmount} onChange={e => setRefundAmount(e.target.value)} helperText={`Max refundable: ${formatCurrency(totalPaid - totalRefunded)}`} data-id="element-89" />
              <TextArea label="Reason" value={refundReason} onChange={e => setRefundReason(e.target.value)} placeholder="Enter refund reason..." data-id="element-90" />
              <div className="flex justify-end gap-2" data-id="element-91">
                <Button variant="secondary" onClick={() => setIsAddingRefund(false)} data-id="element-92">
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleAddRefund} isLoading={isLoading} disabled={!refundAmount || !refundReason || parseFloat(refundAmount) <= 0} data-id="element-93">
                  Issue Refund
                </Button>
              </div>
            </div> : <Button variant="danger" onClick={() => setIsAddingRefund(true)} disabled={totalPaid - totalRefunded <= 0} data-id="element-94">
              Issue Refund
            </Button>}

          <Table columns={refundColumns} data={refunds} keyExtractor={r => r.id} emptyMessage="No refunds issued" data-id="element-95" />
        </div>}
    </Modal>;
}